import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import {
  type DurablePreviewEntry,
  type DurablePreviewState,
  getDurablePreviewEntries,
  isDurablePreviewEventForChat,
  parseDurablePreviewEvent,
  reduceDurablePreviewState,
} from '../lib/durable-preview';

export type DurablePreviewContextValue = {
  previews: DurablePreviewEntry[];
  clear: () => void;
  onData: (part: unknown) => void;
};

const EMPTY_DURABLE_PREVIEW_CONTEXT: DurablePreviewContextValue = {
  previews: [],
  clear: () => {},
  onData: () => {},
};

const DurablePreviewContext = createContext<DurablePreviewContextValue>(EMPTY_DURABLE_PREVIEW_CONTEXT);
const SchedulePreviewHydrationContext = createContext(false);
const EMPTY_DURABLE_PREVIEW_STATE: DurablePreviewState = new Map();

export function DurablePreviewProvider({
  children,
  isSchedulePreviewHydrating = false,
  value,
}: {
  children: ReactNode;
  isSchedulePreviewHydrating?: boolean;
  value: DurablePreviewContextValue;
}) {
  return (
    <DurablePreviewContext.Provider value={value}>
      <SchedulePreviewHydrationContext.Provider value={isSchedulePreviewHydrating}>
        {children}
      </SchedulePreviewHydrationContext.Provider>
    </DurablePreviewContext.Provider>
  );
}

export function useDurablePreviews() {
  return useContext(DurablePreviewContext).previews;
}

export function useIsSchedulePreviewHydrating() {
  return useContext(SchedulePreviewHydrationContext);
}

export function useDurablePreviewState(chatUuid: string): DurablePreviewContextValue {
  const [state, setState] = useState<{ chatUuid: string; previews: DurablePreviewState }>(() => ({
    chatUuid,
    previews: new Map(),
  }));

  // Reset chat-local speculative state before children observe a new chat.
  // React immediately retries this render with the replacement state.
  if (state.chatUuid !== chatUuid) {
    setState({ chatUuid, previews: new Map() });
  }

  const clear = useCallback(() => {
    setState((current) =>
      current.chatUuid === chatUuid && current.previews.size === 0 ? current : { chatUuid, previews: new Map() },
    );
  }, [chatUuid]);

  const onData = useCallback(
    (part: unknown) => {
      const event = parseDurablePreviewEvent(part);
      if (!event || !isDurablePreviewEventForChat(event, chatUuid)) {
        return;
      }

      setState((current) => {
        const previews = reduceDurablePreviewState(
          current.chatUuid === chatUuid ? current.previews : EMPTY_DURABLE_PREVIEW_STATE,
          event,
        );

        return current.chatUuid === chatUuid && previews === current.previews ? current : { chatUuid, previews };
      });
    },
    [chatUuid],
  );

  const previewState = state.chatUuid === chatUuid ? state.previews : EMPTY_DURABLE_PREVIEW_STATE;
  const previews = useMemo(() => getDurablePreviewEntries(previewState), [previewState]);

  return useMemo(() => ({ previews, clear, onData }), [clear, onData, previews]);
}
